// Fleet availability from the F/S partes -- pure, tested by
// disponibilidadLogica.test.mjs. Input: GET /api/partes/disponibilidad
//   { hoy, unidades: [{id, codigo, proyecto, desde, hasta}], intervalos: [{parte_id, numero, nombre, unidad_id, desde, hasta}] }
//
// A bus-day is fuera de servicio (F) when an F/S parte covers it: from its
// incident date (inclusive) to its closing date (exclusive -- back in service
// that day). Every other day of a unit in the fleet is disponible (D). A unit
// is in the fleet from its `desde` (fecha de ingreso) and, dada de baja, up
// to its `hasta`; days after today don't exist yet. Availability = D / (D + F), as in the old spreadsheet.
// Dates are ISO strings 'YYYY-MM-DD' throughout.

const DIA = 86400000
const aMs = (iso) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10))
const aIso = (ms) => new Date(ms).toISOString().slice(0, 10)

export const sumarDias = (iso, n) => aIso(aMs(iso) + n * DIA)
export const minIso = (a, b) => (a < b ? a : b)
export const maxIso = (a, b) => (a > b ? a : b)

// Every day from desde to hasta, both included.
export function diasEntre(desde, hasta) {
  const out = []
  for (let t = aMs(desde), fin = aMs(hasta); t <= fin; t += DIA) out.push(aIso(t))
  return out
}

export const inicioMes = (iso) => iso.slice(0, 8) + '01'
export function finMes(iso) {
  const y = +iso.slice(0, 4), m = +iso.slice(5, 7)
  return aIso(Date.UTC(y, m, 0))
}
export function sumarMeses(iso, n) {
  const y = +iso.slice(0, 4), m = +iso.slice(5, 7) - 1 + n
  return aIso(Date.UTC(y + Math.floor(m / 12), ((m % 12) + 12) % 12, 1))
}

// The same day n months earlier (clamped: 31 May - 3 months = 28/29 Feb).
export function mesesAtras(iso, n) {
  const primero = sumarMeses(inicioMes(iso), -n)
  return minIso(primero.slice(0, 8) + iso.slice(8, 10), finMes(primero))
}

// The periods of the KPI cards, each { desde, hasta } ending today:
// the month and year so far, and the last 3 and 12 months (rolling).
export function periodos(hoy) {
  return {
    mes: { desde: inicioMes(hoy), hasta: hoy },
    tresMeses: { desde: sumarDias(mesesAtras(hoy, 3), 1), hasta: hoy },
    anio: { desde: hoy.slice(0, 4) + '-01-01', hasta: hoy },
    doceMeses: { desde: sumarDias(mesesAtras(hoy, 12), 1), hasta: hoy },
  }
}

// The units to show: project filter ('' = all) and code search.
export function filtrarUnidades(unidades, { proyecto = '', buscar = '' } = {}) {
  const b = buscar.trim().toLowerCase()
  return unidades.filter(u => (!proyecto || u.proyecto === proyecto) && (!b || u.codigo.toLowerCase().includes(b)))
}

// intervalos grouped by unit, each list sorted by desde.
export function intervalosPorUnidad(intervalos) {
  const m = new Map()
  for (const i of intervalos) {
    if (!m.has(i.unidad_id)) m.set(i.unidad_id, [])
    m.get(i.unidad_id).push(i)
  }
  for (const l of m.values()) l.sort((a, b) => (a.desde < b.desde ? -1 : a.desde > b.desde ? 1 : 0))
  return m
}

// One unit's days in [desde, hasta]: [{ dia, estado: 'D'|'F'|null, parte }]
// (null = not in the fleet that day, or in the future; parte = the F/S
// interval covering it).
export function diasDeUnidad(unidad, suyos, desde, hasta, hoy) {
  return diasEntre(desde, hasta).map(dia => {
    if (dia > hoy || (unidad.desde && dia < unidad.desde) || (unidad.hasta && dia > unidad.hasta)) return { dia, estado: null, parte: null }
    const parte = (suyos || []).find(i => i.desde <= dia && (!i.hasta || dia < i.hasta)) || null
    return { dia, estado: parte ? 'F' : 'D', parte }
  })
}

// Counts of a list of days (or of 'D'/'F' letters).
export function contar(dias) {
  let d = 0, f = 0
  for (const x of dias) {
    const e = x && typeof x === 'object' ? x.estado : x
    if (e === 'D') d++
    else if (e === 'F') f++
  }
  return { disp: d, fs: f, pct: d + f ? d / (d + f) : null }
}

// The grid of a period: one row per unit with its days and totals, plus the
// fleet's total per day and overall.
export function matriz(datos, unidades, desde, hasta) {
  const porU = intervalosPorUnidad(datos.intervalos)
  const filas = unidades.map(u => {
    const dias = diasDeUnidad(u, porU.get(u.id), desde, hasta, datos.hoy)
    return { unidad: u, dias, ...contar(dias) }
  })
  const dias = diasEntre(desde, hasta)
  const porDia = dias.map((dia, k) => ({ dia, ...contar(filas.map(f => f.dias[k])) }))
  return { dias, filas, porDia, total: sumar(filas) }
}

const sumar = (lista) => {
  const disp = lista.reduce((s, x) => s + x.disp, 0), fs = lista.reduce((s, x) => s + x.fs, 0)
  return { disp, fs, pct: disp + fs ? disp / (disp + fs) : null }
}

// Availability of a set of units over a period, without the grid.
export function disponibilidad(datos, unidades, desde, hasta) {
  if (desde > hasta) return { disp: 0, fs: 0, pct: null }
  return matriz(datos, unidades, desde, hasta).total
}

// A year by month: for each month { mes: 'YYYY-MM', filas totals per unit
// (by id), total }, months after today empty.
export function porMes(datos, unidades, anio) {
  const porU = intervalosPorUnidad(datos.intervalos)
  const meses = []
  for (let m = 1; m <= 12; m++) {
    const desde = `${anio}-${String(m).padStart(2, '0')}-01`
    const hasta = finMes(desde)
    const porUnidad = {}
    for (const u of unidades) porUnidad[u.id] = contar(diasDeUnidad(u, porU.get(u.id), desde, hasta, datos.hoy))
    meses.push({ mes: desde.slice(0, 7), desde, hasta, porUnidad, total: sumar(Object.values(porUnidad)) })
  }
  return meses
}

// F/S partes of a period: opened in it, and the average days out of service
// of those closed in it (MTTR).
export function indicadoresPartes(datos, unidades, desde, hasta) {
  const ids = new Set(unidades.map(u => u.id))
  const suyos = datos.intervalos.filter(i => ids.has(i.unidad_id))
  const abiertos = suyos.filter(i => i.desde >= desde && i.desde <= hasta).length
  const cerrados = suyos.filter(i => i.hasta && i.hasta >= desde && i.hasta <= hasta)
  const mttr = cerrados.length
    ? cerrados.reduce((s, i) => s + (aMs(i.hasta) - aMs(i.desde)) / DIA, 0) / cerrados.length
    : null
  const fueraHoy = suyos.filter(i => i.desde <= datos.hoy && (!i.hasta || datos.hoy < i.hasta)).length
  return { abiertos, cerrados: cerrados.length, mttr, fueraHoy }
}

// 0.9347 -> '93,5 %'
export function formatoPct(p, decimales = 1) {
  if (p == null) return '—'
  return (p * 100).toFixed(decimales).replace('.', ',') + ' %'
}

// A color band for an availability, for the cells and cards.
export function banda(p) {
  if (p == null) return 'nada'
  if (p >= 0.95) return 'alta'
  if (p >= 0.9) return 'media'
  if (p >= 0.8) return 'baja'
  return 'critica'
}
